<template>
  <div class="fund-detail-overlay" v-if="visible && fund && fund.code" @click.self="$emit('close')">
    <div class="detail-card">
      <div class="detail-header">
        <div class="back-btn" @click="$emit('close')">
          <span class="icon">✕</span>
        </div>
        <div class="header-title">基金详情</div>
        <div class="action-btn" @click="$emit('action')">编辑</div>
      </div>

      <div class="detail-content">
        <div class="fund-info-card">
          <div class="fund-title">{{ fund.name }}</div>
          <div class="fund-code">{{ fund.code }}</div>
          
          <div class="fund-main-stats">
            <div class="stat-item">
              <div class="label">估算涨幅</div>
              <div class="value" :class="getProfitClass(fund.dailyRate)">
                {{ formatRate(fund.dailyRate) }}%
              </div>
            </div>
            <div class="stat-item">
              <div class="label">净值估算</div>
              <div class="value">{{ fund.netValue }}</div>
            </div>
          </div>
        </div>

        <div class="holdings-section">
          <div class="section-title">前十大持仓股票</div>
          
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            加载中...
          </div>
          
          <div v-else-if="error" class="error-state">
            {{ error }}
          </div>
          
          <div v-else class="holdings-table">
            <div class="table-header">
              <div class="col-name">股票名称</div>
              <div class="col-percent text-right">占比</div>
              <div class="col-change text-right">涨跌幅</div>
            </div>
            <div class="table-body">
              <div class="table-row" v-for="(stock, index) in stocks" :key="index">
                <div class="col-name">
                  <div class="stock-name">{{ stock.name }}</div>
                  <div class="stock-code">{{ stock.code }}</div>
                </div>
                <div class="col-percent text-right">{{ stock.percent }}%</div>
                <div class="col-change text-right" :class="getProfitClass(stock.zdf)">
                  {{ parseFloat(stock.zdf) > 0 ? '+' : '' }}{{ stock.zdf }}%
                </div>
              </div>
            </div>
          </div>
          
          <div class="data-source-tip">数据来源于公开网络，仅供参考，投资需谨慎</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '../utils/db';
import { formatRate, getProfitClass } from '../utils/format';

export default {
  name: 'FundDetail',
  props: {
    visible: Boolean,
    fund: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      stocks: [],
      loading: false,
      error: null
    }
  },
  watch: {
    visible(val) {
      if (val && this.fund && this.fund.code) {
        this.fetchHoldings();
      }
    }
  },
  methods: {
    formatRate: formatRate,
    getProfitClass: getProfitClass,
    buildSecids(stocks) {
      if (!Array.isArray(stocks)) return '';
      return stocks
        .map(item => {
          const code = item && item.code ? String(item.code).trim() : '';
          if (!/^\d{6}$/.test(code)) return null;
          const market = (code.startsWith('6') || code.startsWith('9')) ? '1' : '0';
          return `${market}.${code}`;
        })
        .filter(Boolean)
        .join(',');
    },
    async fetchQuotesBySecids(secids) {
      if (!secids) return;
      const res = await fetch(`/api/holdings?secids=${secids}`);
      if (!res.ok) return;
      const data = await res.json();
      const quotes = data.quotes || {};
      if (!quotes || Object.keys(quotes).length === 0) return;
      this.stocks = this.stocks.map(stock => {
        const zdf = quotes[stock.code];
        if (zdf === undefined) return stock;
        return { ...stock, zdf };
      });
    },
    async fetchHoldings() {
      this.loading = true;
      this.error = null;
      this.stocks = [];
      
      try {
        // 1. 尝试从本地数据库获取
        const cachedData = await db.getHoldings(this.fund.code);
        
        // 检查缓存是否有效 (例如 24小时过期)
        const CACHE_DURATION = 24 * 60 * 60 * 1000;
        const now = Date.now();
        
        if (cachedData && cachedData.stocks && (now - cachedData.updatedAt < CACHE_DURATION)) {
          // console.log(`[FundDetail] Using cached holdings for ${this.fund.code}`);
          this.stocks = cachedData.stocks;
          
          // 如果有缓存，仍然可以在后台悄悄更新（可选），或者直接返回
          // 这里我们选择直接返回，减轻服务器压力
          this.loading = false;
          const secids = this.buildSecids(this.stocks);
          this.fetchQuotesBySecids(secids);
          return;
        }

        // 2. 如果没有缓存或过期，从 API 获取
        // console.log(`[FundDetail] Fetching holdings from API for ${this.fund.code}`);
        const res = await fetch(`/api/holdings?code=${this.fund.code}`);
        if (!res.ok) throw new Error('Network error');
        const data = await res.json();
        
        if (data.error) {
          throw new Error(data.error);
        }
        
        const newStocks = data.stocks || [];
        this.stocks = newStocks;
        
        // 3. 保存到本地数据库
        if (newStocks.length > 0) {
          await db.saveHoldings(this.fund.code, newStocks);
          // console.log(`[FundDetail] Saved holdings to DB for ${this.fund.code}`);
        }
        
      } catch (err) {
        console.error('Fetch holdings failed:', err);
        this.error = '暂无持仓数据';
        // 如果 API 失败但有旧缓存，也可以降级显示旧缓存
        if (!this.stocks.length) {
           const oldCache = await db.getHoldings(this.fund.code);
           if (oldCache && oldCache.stocks) {
             this.stocks = oldCache.stocks;
             this.error = null; // 清除错误，因为显示了旧数据
           }
        }
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.fund-detail-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
  animation: fadeIn 0.2s ease-out;
}

.detail-card {
  width: 100%;
  max-width: 600px;
  background: white;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  height: 80vh;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

/* 移动端适配 */
@media (max-width: 768px) {
  .fund-detail-overlay {
    padding: 0;
    align-items: flex-end;
  }
  
  .detail-card {
    height: 90vh;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.detail-header {
  background: white;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.back-btn {
  font-size: 20px;
  color: #666;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
}

.back-btn:hover {
  background: #f5f5f5;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.action-btn {
  font-size: 14px;
  color: #1890ff;
  cursor: pointer;
  padding: 4px 12px;
  border-radius: 14px;
  background: rgba(24, 144, 255, 0.1);
  font-weight: 500;
}

.detail-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-color: #f7f8fa;
}

.fund-info-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.02);
  text-align: center;
}

.fund-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #1f1f1f;
}

.fund-code {
  font-size: 13px;
  color: #999;
  margin-bottom: 24px;
  font-family: monospace;
}

.fund-main-stats {
  display: flex;
  justify-content: center;
  gap: 40px;
  padding-top: 16px;
  border-top: 1px solid #f5f5f5;
}

.stat-item .label {
  font-size: 12px;
  color: #999;
  margin-bottom: 8px;
}

.stat-item .value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.holdings-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.02);
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: '';
  display: block;
  width: 4px;
  height: 14px;
  background: #1890ff;
  border-radius: 2px;
  margin-right: 8px;
}

.holdings-table {
  width: 100%;
}

.table-header {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #999;
  font-size: 12px;
}

.table-row {
  display: flex;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #f9f9f9;
  font-size: 14px;
  color: #333;
}

.table-row:last-child {
  border-bottom: none;
}

.col-name {
  flex: 4;
  text-align: left;
  overflow: hidden;
}

.col-percent {
  flex: 3;
}

.col-change {
  flex: 3;
}

.stock-name {
  font-size: 14px;
  color: #333;
  margin-bottom: 2px;
  font-weight: 500;
}

.stock-code {
  font-size: 12px;
  color: #bfbfbf;
}

.text-right {
  text-align: right;
}

.data-source-tip {
  text-align: center;
  color: #d9d9d9;
  font-size: 12px;
  margin-top: 24px;
  margin-bottom: 10px;
}

.loading-state, .error-state {
  text-align: center;
  padding: 40px 0;
  color: #999;
  font-size: 14px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f0f0f0;
  border-top-color: #1890ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 10px;
}

.red { color: #ff4d4f; }
.green { color: #52c41a; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
